import Vue from 'vue';
import { mapActions } from 'vuex';
import NameConstructor from '@/views/modules/_common/name.constructor';

import OptionsDropDownFilter from '@/views/modules/contacts/filters/OptionsDropDownFilter';

/**
 * Tags dropdown filter
 *
 * @module views/modules/contacts/contact/filters/TagsDropDownFilter
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 */
export default Vue.extend({
  name: 'TagsDropDownFilter',
  template: `
    <options-drop-down-filter
      ref="OptionsDropDownFilter"
      id="tags-drop-down-filter-wrapper"
      :params="agGridParams"
      :selected-items="selectedTags"
      :fetch-function="fetchAllTags"
      :filter-condition-options="filterConditionOptions"
      :placeholder="$t('$ContactModule.SelectTags')"
      :manage-text="$t('$ContactModule.ManageAudienceTags')"
      manage-route-name="tags"
      :manage-redirect-only-event="manageRedirectOnlyEvent"
      :disabled="disabled"
      :filter-type="$enums.AppFilterType.TAGS"
      :ag-grid-floating-filter="agGridFloatingFilter"
      @blur="$emit('blur', $event)"
      @redirect="$emit('redirect', $event)"
      @create="addTag"
      @input="selectedTagsChanged">
    </options-drop-down-filter>
  `,
  components: {
    OptionsDropDownFilter,
  },
  props: {
    selectedTags: {
      type: Array,
      required: false,
      default() {
        return [];
      },
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    manageRedirectOnlyEvent: {
      type: Boolean,
      required: false,
      default: false,
    },
    agGridFloatingFilter: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      filterConditionOptions: Object.values(
        this.$enums.AppFilter.FilterType.Tags.Type,
      ).map((condition) => ({
        label: this.$t(`$AppFilters.$Operation.${condition}`),
        value: condition,
      })),
    };
  },
  computed: {
    agGridParams() {
      return this.params || undefined;
    },
  },
  methods: {
    ...mapActions({
      fetchAllTags: 'tag/fetchAllTags',
      addTagToSelectedAudience: 'tag/addTag',
    }),
    async addTag(name) {
      // Validate that the tag name is not empty or just whitespace
      if (!name || typeof name !== 'string' || name.trim() === '') {
        this.$showGeneralErrorNotification({
          title: 'Tag creation failed',
          text: 'Tag name cannot be empty. Please enter a valid tag name.',
        });
        return;
      }

      const trimmedName = name.trim();

      try {
        const newTag = new NameConstructor(trimmedName);
        await this.addTagToSelectedAudience(newTag);
        await this.$refs.OptionsDropDownFilter.resetSearch();

        // Show success notification
        this.$showSuccessActionNotification({
          title: 'Tag created',
          text: `Tag "${trimmedName}" has been created successfully.`,
        });
      } catch (error) {
        // Handle specific validation errors
        let errorMessage = 'Failed to create tag. Please try again.';

        if (error.response && error.response.data && error.response.data.message) {
          const backendMessage = error.response.data.message;
          // Check if it's a validation error about empty name
          if (backendMessage.includes('name') && backendMessage.includes('empty')) {
            errorMessage = 'Tag name cannot be empty. Please enter a valid tag name.';
          } else if (backendMessage.includes('name') && backendMessage.includes('required')) {
            errorMessage = 'Tag name is required. Please enter a valid tag name.';
          } else {
            errorMessage = backendMessage;
          }
        }

        this.$showGeneralErrorNotification({
          title: 'Tag creation failed',
          text: errorMessage,
        });
      }
    },
    selectedTagsChanged(tags) {
      this.$emit('update:selected-tags', tags);
    },
    getModel() {
      return this.$refs.OptionsDropDownFilter.getModel();
    },
    setModel(model) {
      this.$refs.OptionsDropDownFilter.setModel(model);
    },
  },
});
